import { Component, forwardRef, HostListener, Injector, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { KeyCodes } from '../../../constants/keycodes.constants';
import { BaseFormControlComponent } from '../base-form-control.component';

@Component({
    selector: 'tag-input',
    styleUrls: ['./tag-input.component.scss'],
    template: `
        <lsq-label [name]="name" [label]="label" [subLabel]="subLabel"></lsq-label>
        <div
            class="tag-input"
            [class.error]="errorMessage"
            [class.focused]="inputFocused || selectedTagIndex !== null"
        >
            <div
                *ngFor="let value of model; let i = index"
                class="tag"
                [class.selected]="i === selectedTagIndex"
                [class.error]="tagIndexesWithError.includes(i)"
                (click)="selectedTagIndex = i"
            >
                {{ value }}
            </div>
            <input
                type="text"
                [placeholder]="placeholder"
                [(ngModel)]="inputValue"
                (blur)="handleInputBlur()"
                (focus)="inputFocused = true"
                (paste)="handlePaste($event)"
            />
        </div>
        <div *ngIf="errorMessage" class="error-message">{{ errorMessage }}</div>
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TagInputComponent),
            multi: true
        }
    ]
})
export class TagInputComponent extends BaseFormControlComponent<string[]> {
    @Input() public name?: string;
    @Input() public label?: string;
    @Input() public subLabel?: string;
    @Input() public placeholder?: string;

    // TODO(PDEV-223): When refactoring BaseFormControl, do validation the proper way.
    @Input() public tagIndexesWithError: number[] = [];
    @Input() public errorMessage?: string;

    public inputValue: string;
    public selectedTagIndex: number = null;
    public inputFocused = false;

    @HostListener('document:keydown', ['$event'])
    public handleKeyboardEvent(event: KeyboardEvent): void {
        if (this.inputFocused || this.selectedTagIndex !== null) {
            this.handleKeyPress(event);
        }
    }

    constructor(public injector: Injector) {
        super(injector);
    }

    public handleKeyPress(event): void {
        const key = event.keyCode;

        switch (key) {
            case KeyCodes.Backspace:
                this._handleBackspace();
                break;
            case KeyCodes.Space:
            case KeyCodes.Enter:
            case KeyCodes.Comma:
            case KeyCodes.Semicolon:
                if (!!this.inputValue) {
                    this._addValue(this.inputValue);
                }
                event.preventDefault();
                break;
            case KeyCodes.Tab:
                if (!!this.inputValue) {
                    this._addValue(this.inputValue);
                    event.preventDefault();
                }
                break;
            case KeyCodes.LeftArrow:
                if (!this.inputValue) {
                    this._handleLeftArrow();
                    event.preventDefault();
                }
                break;
            case KeyCodes.RightArrow:
                if (!this.inputValue) {
                    this._handleRightArrow();
                    event.preventDefault();
                }
                break;
            default:
                this.selectedTagIndex = null;
                break;
        }
    }

    public handleInputBlur(): void {
        this.selectedTagIndex = null;
        this.inputFocused = false;
        if (this.inputValue) {
            this._addValue(this.inputValue);
        }
    }

    public handlePaste(event: ClipboardEvent): void {
        const delimiterOptions = ['; ', ', ', ',', ';', ' '];
        const pastedText = event.clipboardData.getData('text');

        let hasDelimiter = false;
        let valuesToAdd = [];
        for (let i = 0; i < delimiterOptions.length; i++) {
            const option = delimiterOptions[i];
            if (pastedText.includes(option)) {
                hasDelimiter = true;
                valuesToAdd = pastedText.split(option).map((value) => value.trim());
                break;
            }
        }
        if (hasDelimiter) {
            valuesToAdd.forEach((value) => {
                this._addValue(value);
            });
        } else this._addValue(pastedText);

        event.preventDefault();
    }

    private _addValue(value: string): void {
        this.model.push(value);
        this.handleChange(this.model);
        this.inputValue = null;
    }

    private _removeValue(): void {
        if (this.selectedTagIndex !== null) {
            this.model.splice(this.selectedTagIndex, 1);
        } else {
            this.model.splice(this.model.length - 1, 1);
        }
        this.handleChange(this.model);

        if (this.selectedTagIndex !== 0 && this.selectedTagIndex !== null) {
            this.selectedTagIndex--;
        }
    }

    private _handleBackspace() {
        if (!this.inputValue && this.model.length) {
            this._removeValue();
        }
    }

    private _handleLeftArrow(): void {
        if (!this.model.length) {
            return;
        } else if (this.selectedTagIndex === null) {
            this.selectedTagIndex = this.model.length - 1;
        } else if (this.selectedTagIndex > 0) {
            this.selectedTagIndex--;
        }
    }

    private _handleRightArrow(): void {
        if (!this.model.length || this.selectedTagIndex === null) {
            return;
        } else if (this.selectedTagIndex === this.model.length - 1) {
            if (this.inputFocused) {
                this.selectedTagIndex = null;
            } else {
                return;
            }
        } else if (this.selectedTagIndex < this.model.length - 1) {
            this.selectedTagIndex++;
        }
    }
}
